import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Modal, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, ClockCircleOutlined, FundOutlined, StopOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/currency';
import { authService } from '../../../services/auth';
import { CostPartialMark, ReportFigure, ReportSection } from '../ReportTab';
import type { IncomeStatementResponse } from './incomeStatementApi';
import { forecastMonthsLabel } from './incomeStatement';
import {
  buildOrdersInResult,
  ordersInResultTotals,
  rowsInMonths,
  type OrderInResultRow,
} from './ordersInResult';
import OrderPivotSection from './OrderPivotSection';
import type { PivotOrderInfo } from './orderPivot';
import './OrdersInResultSection.css';

interface Props {
  /** Account manager and product lines per order (from the order list); enables the pivot. */
  orderInfo?: Map<string, PivotOrderInfo>;
  /** The income statement of the period's year, loaded once by the page (null while loading). */
  statement: IncomeStatementResponse | null;
  failed?: boolean;
  /** The months of the period of „Wynik firmy” (`YYYY-MM`). */
  months: string[];
  /** The period is the whole year: orders outside the sums (they have no month) are shown. */
  wholeYear: boolean;
  periodLabel: string;
}

// „Sprzedaż i zamówienia”: the period's orders in the result (actual =
// approved sales invoice, forecast = waiting for it, outside the sums) as
// figures and „Przekrój sprzedaży”. Since PR2 (2026-10-29) the period is the
// one of the whole page (year, quarter or month, a month bar of the Pulpit
// included) and the year's income statement is the one the Pulpit uses.
const OrdersInResultSection: React.FC<Props> = ({ orderInfo, statement, failed, months: periodMonths, wholeYear, periodLabel }) => {
  const { t, i18n } = useTranslation(['reports', 'orders', 'common']);
  const language = i18n.language || 'pl';
  const permitted = authService.canViewForecastVsActual();
  const k = (key: string, options?: Record<string, unknown>) => t(`reports:profitability.ordersInResult.${key}`, options ?? {}) as string;
  const data = permitted ? statement : null;
  const [outsideOpen, setOutsideOpen] = useState(false);

  const allRows = useMemo(() => (data ? buildOrdersInResult(data) : []), [data]);
  // The period's months of the loaded year; its „*” after they are summed.
  const rows = useMemo(() => (wholeYear ? allRows : rowsInMonths(allRows, periodMonths)), [allRows, periodMonths, wholeYear]);
  const outsideRows = useMemo(() => allRows.filter((row) => row.place === 'OUTSIDE'), [allRows]);
  const totals = useMemo(() => ordersInResultTotals(rows), [rows]);
  const months = (list: string[]) => forecastMonthsLabel(list, language);
  // Figure notes are one line: whole złoty there, exact amounts in the pivot.
  const roundZl = (amount: number) => `${Math.round(amount).toLocaleString('pl-PL')} zł`;

  // Why an order is where it is: the place and its months, then the display flags.
  function reasons(row: OrderInResultRow): string[] {
    const lines: string[] = [];
    if (row.place === 'OUTSIDE') {
      const named = row.reviewReasons.map((reason) => t(`reports:profitability.incomeStatement.outsidePlan.reasons.${reason}`, { defaultValue: reason }));
      lines.push(named.length ? k('why.outsideReasons', { reasons: named.join(', ') }) : k('why.outside'));
    } else if (row.place === 'ACTUAL') lines.push(k('why.actual', { months: months(row.actual?.months || []) }));
    else if (row.place === 'BOTH') lines.push(k('why.both', { actualMonths: months(row.actual?.months || []), forecastMonths: months(row.forecast?.months || []) }));
    else lines.push(k('why.forecast', { months: months(row.forecast?.months || []) }));
    row.flags.forEach((flag) => lines.push(k(`flagWhy.${flag}`)));
    return lines;
  }
  // The pivot explains the whole order, not only the chosen month.
  const allById = useMemo(() => new Map(allRows.map((row) => [row.id, row])), [allRows]);
  const whyOf = (row: OrderInResultRow) => reasons(allById.get(row.id) ?? row);

  const outsideColumns: ColumnsType<OrderInResultRow> = [
    {
      title: k('colOrder'),
      key: 'order',
      width: '34%',
      render: (_, row) => (
        <div className="orders-in-result__order">
          <Link to={`/app/orders/${row.id}`}>{row.displayOrderNumber}</Link>
          <span className="report-cut" title={row.customerName}>{row.customerName}</span>
        </div>
      ),
    },
    {
      title: k('colWhy'),
      key: 'why',
      render: (_, row) => (
        <div className="orders-in-result__why">{reasons(row).map((line) => <span key={line}>{line}</span>)}</div>
      ),
    },
  ];

  if (!permitted) {
    return (
      <ReportSection title={k('title')} icon={<UnorderedListOutlined />} lead={k('adminOnly')}>
        <span className="report-muted">{k('adminOnly')}</span>
      </ReportSection>
    );
  }

  const partialHint = t('reports:managementDashboard.notes.incompleteProfitHint') as string;
  return (
    <div className="orders-in-result">
      {failed && <Alert type="error" showIcon message={k('loadFailed')} />}
      {/* Before the report arrives there is nothing to sum: no figures, not zeros. */}
      {!data && !failed && <div className="orders-in-result__loading"><Spin /></div>}
      {data && (
        <div className="report-figures">
          <ReportFigure
            icon={<CheckCircleOutlined />}
            tone="green"
            testId="orders-in-result-actual"
            label={k('figureActual', { count: totals.actual.count })}
            value={formatCurrency(totals.actual.revenue, 'PLN')}
            note={k(totals.actual.unknownCostCount ? 'figureActualNoteUnknown' : 'figureActualNote', {
              margin: roundZl(totals.actual.margin), count: totals.actual.unknownCostCount,
            })}
            noteTitle={totals.actual.unknownCostCount ? `${k('figureActualNoteUnknown', { margin: roundZl(totals.actual.margin), count: totals.actual.unknownCostCount })}. ${partialHint}` : undefined}
          />
          <ReportFigure
            icon={<ClockCircleOutlined />}
            tone="blue"
            testId="orders-in-result-forecast"
            label={k('figureForecast', { count: totals.forecast.count })}
            value={formatCurrency(totals.forecast.revenue, 'PLN')}
            note={k(totals.forecast.unknownCostCount ? 'figureForecastNoteUnknown' : 'figureForecastNote', {
              margin: roundZl(totals.forecast.margin), count: totals.forecast.unknownCostCount,
            })}
            noteTitle={totals.forecast.unknownCostCount ? `${k('figureForecastNoteUnknown', { margin: roundZl(totals.forecast.margin), count: totals.forecast.unknownCostCount })}. ${partialHint}` : undefined}
          />
          <ReportFigure
            icon={<FundOutlined />}
            tone="indigo"
            testId="orders-in-result-total"
            label={k('figureTotal', { count: totals.totalCount })}
            value={formatCurrency(totals.actual.revenue + totals.forecast.revenue, 'PLN')}
            note={(
              <>
                {k('figureTotalNote', { margin: roundZl(totals.actual.margin + totals.forecast.margin) })}
                {totals.totalPendingCount > 0 && <CostPartialMark hint={k('figureTotalPartial')} testId="orders-in-result-total-partial" />}
              </>
            )}
          />
          {/* Outside orders have no month: a tile of the whole year only (a quarter or a month has three, always). */}
          {wholeYear && (
            <ReportFigure
              icon={<StopOutlined />}
              tone="gray"
              label={k('figureOutside')}
              value={k('ordersCount', { count: outsideRows.length })}
              note={outsideRows.length > 0
                ? <Button type="link" size="small" className="orders-in-result__outside-link" onClick={() => setOutsideOpen(true)}>{k('showOutside')}</Button>
                : k('figureOutsideNote')}
            />
          )}
        </div>
      )}
      {orderInfo && data && <OrderPivotSection rows={rows} orderInfo={orderInfo} periodLabel={periodLabel} whyOf={whyOf} />}
      <Modal
        open={outsideOpen}
        title={k('outsideTitle', { count: outsideRows.length })}
        footer={null}
        width={760}
        onCancel={() => setOutsideOpen(false)}
      >
        <p className="report-muted">{k('figureOutsideNote')}</p>
        <div className="report-table orders-in-result__outside">
          <Table<OrderInResultRow>
            rowKey="id"
            size="small"
            tableLayout="fixed"
            columns={outsideColumns}
            dataSource={outsideRows}
            pagination={outsideRows.length > 10 ? { pageSize: 10 } : false}
          />
        </div>
      </Modal>
    </div>
  );
};

export default OrdersInResultSection;
